import { formatTimeSpanNormal, useLiveCounter } from "@buzzeasy/shared-frontend-utilities";
import { Space, theme, Typography } from "antd";
import { AutoCenter, Button } from "antd-mobile";
import { FC, useCallback, useState } from "react";
import { styled } from "styled-components";
import { routes } from "../../App";
import SideMenu from "../../components/containers/SideMenu";
import CenterContent from "../../components/layouts/CenterContent";
import LayoutWithNav from "../../components/layouts/LayoutWithNav";
import BreakList from "../../components/presenters/BreakList";
import { useGetAgentStateQuery, useGetBreakNamesQuery, useGoToAvailableMutation, useGoToBreakMutation } from "../../redux/apis/blenderApi";

const { Title } = Typography;

const HomePage: FC = () => {
  const { token } = theme.useToken();

  const { data: agentState } = useGetAgentStateQuery();
  const { data: breakNames } = useGetBreakNamesQuery();

  const [goToAvailable] = useGoToAvailableMutation();
  const [goToBreak] = useGoToBreakMutation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBreakListOpen, setIsBreakListOpen] = useState(false);

  const stateTimer = useLiveCounter(agentState?.timedBreakExpiresAt ?? agentState?.stateSince ?? new Date());
  const shouldAddBreakText = !agentState?.breakName?.toLowerCase().endsWith("break");

  const isOnBreak = agentState?.state === "Break";
  const backgroundColor = isOnBreak ? token.colorWarning : token.colorSuccess;
  const imgSrc = isOnBreak ? !agentState.breakName ? "/img/just-logged-in.png" : "/img/on-break.png" : "/img/available.png";
  const statusText = isOnBreak ? !agentState.breakName ? "Just logged in" : `On ${agentState.breakName} ${shouldAddBreakText ? "break" : ""}` : "Waiting for incoming task";
  const btnText = isOnBreak ? !agentState.breakName ? "Start working" : "End break" : "Go to break";

  const handleClick = useCallback(
    () => {
      if (isOnBreak)
        void goToAvailable();
      else
        setIsBreakListOpen(true);
    },
    [goToAvailable, isOnBreak],
  );

  const handleSelectBreak = useCallback(
    (breakName: string) => {
      setIsBreakListOpen(false);
      void goToBreak(breakName);
    },
    [goToBreak],
  );

  return (
    <LayoutWithNav
      backgroundColor={backgroundColor}
      mainContent={
        <CenterContent $backgroundColor={backgroundColor} $color={token.colorText} $padding={token.padding}>
          <Space direction="vertical" size="large" align="center">
            <Space direction="vertical" size="middle" align="center">
              <AutoCenter style={{ textAlign: "center" }}>
                <Title level={2}>{statusText}</Title>
              </AutoCenter>
              <Title level={5}>{formatTimeSpanNormal(agentState?.timedBreakExpiresAt ? Math.min(0, stateTimer) : Math.max(0, stateTimer))}</Title>
            </Space>
            <ImageContainer $activeImageSrc={imgSrc}>
              <img src="/img/just-logged-in.png" alt="" />
              <img src="/img/on-break.png" alt="" />
              <img src="/img/available.png" alt="" />
            </ImageContainer>
            <div>
              <Button disabled={isBreakListOpen} onClick={handleClick}>{btnText}</Button>
              {
                isBreakListOpen &&
                <BreakList
                  breakNames={breakNames ?? []}
                  onSelectBreak={handleSelectBreak}
                  onTapOutside={() => setIsBreakListOpen(false)}
                />
              }
            </div>
          </Space>
        </CenterContent>
      }
      menuContent={<SideMenu activeTabKey={routes.home} onCloseMenu={() => setIsMenuOpen(false)} />}
      isMenuOpen={isMenuOpen}
      onMenuOpenChange={setIsMenuOpen}
    />
  );
};

export default HomePage;

const ImageContainer = styled.div<{ $activeImageSrc: string }>`
width: 256px;
height: 256px;
position: relative;

& > img {
  position: absolute;
  left: 0;
  top: 0;
  width: 256px;
  height: 256px;
  object-fit: contain;

  animation-duration: ${p => p.theme.motionDurationFast};
  animation-timing-function: ${p => p.theme.motionEaseInOut};
  animation-fill-mode: both;

  animation-name: img-disappear;

  &[src="${p => p.$activeImageSrc}"] {
    animation-name: img-appear;
  }

  @keyframes img-appear {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes img-disappear {
    from { opacity: 1; }
    to { opacity: 0; }
  }
}
`;
